/**
 * LONCOM Smooth Scroll - Lenis + GSAP ScrollTrigger Integration
 * 
 * Features:
 * - Buttery smooth momentum scrolling
 * - Full GSAP ScrollTrigger integration
 * - Smooth anchor link navigation
 * - Accessibility: respects prefers-reduced-motion
 * - Mobile-optimized touch scrolling
 * - Dynamic content support
 * - Form input protection (no scroll hijacking)
 * 
 * @version 1.0.0
 */

(function() {
  'use strict';

  // ============================================================
  // CONFIGURATION
  // Adjust these values to fine-tune the scroll feel
  // ============================================================
  const CONFIG = {
    // Smoothness: 0.05 = very smooth, 0.15 = more responsive
    // Recommended: 0.07-0.1 for premium feel
    lerp: 0.08,
    
    // Duration for scroll-to animations (in seconds)
    duration: 1.2,
    
    // Easing for scroll-to animations
    // Options: 'linear', or custom easing function
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    
    // Wheel multiplier: higher = faster scroll
    wheelMultiplier: 1,
    
    // Touch multiplier for mobile: lower = more controlled
    touchMultiplier: 1.5,
    
    // Infinite scroll (usually false for websites)
    infinite: false,
    
    // Orientation
    orientation: 'vertical',
    
    // Gesture orientation (for touch)
    gestureOrientation: 'vertical',
    
    // Smooth wheel scrolling
    smoothWheel: true,
    
    // Sync touch to native scroll (better mobile feel)
    syncTouch: true,
    
    // Sync touch lerp (0 = instant, 1 = very smooth)
    syncTouchLerp: 0.075,
    
    // Prevent scroll on touch move (set false for better form UX)
    prevent: false,
  };

  // ============================================================
  // GLOBAL STATE
  // ============================================================
  let lenis = null;
  let rafId = null;
  let gsapTickerCb = null;
  let isInitialized = false;
  let isReducedMotion = false;

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================
  
  /**
   * Check if user prefers reduced motion
   */
  function checkReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Debounce function for resize events
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Check if element is a form input
   */
  function isFormElement(element) {
    const formTags = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'];
    return formTags.includes(element.tagName) || 
           element.isContentEditable ||
           element.closest('[data-lenis-prevent]');
  }

  /**
   * Check if viewport is mobile-sized (matches Shopify's mobile breakpoint)
   */
  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  // ============================================================
  // LENIS INITIALIZATION
  // ============================================================
  
  function initLenis() {
    // Skip if already initialized
    if (isInitialized) {
      console.warn('[Smooth Scroll] Already initialized');
      return;
    }

    // Check for reduced motion preference
    isReducedMotion = checkReducedMotion();
    
    if (isReducedMotion) {
      console.log('[Smooth Scroll] Reduced motion detected - smooth scroll disabled');
      document.documentElement.classList.add('reduced-motion');
      return;
    }

    // Skip Lenis on mobile — native scroll performs better on touch devices
    if (isMobileViewport()) {
      console.log('[Smooth Scroll] Mobile detected - using native scroll');
      setupMobileOptimizations();
      return;
    }

    // Check if Lenis is loaded
    if (typeof Lenis === 'undefined') {
      console.error('[Smooth Scroll] Lenis library not found');
      return;
    }

    try {
      // Initialize Lenis
      lenis = new Lenis({
        lerp: CONFIG.lerp,
        duration: CONFIG.duration,
        easing: CONFIG.easing,
        wheelMultiplier: CONFIG.wheelMultiplier,
        touchMultiplier: CONFIG.touchMultiplier,
        infinite: CONFIG.infinite,
        orientation: CONFIG.orientation,
        gestureOrientation: CONFIG.gestureOrientation,
        smoothWheel: CONFIG.smoothWheel,
        syncTouch: CONFIG.syncTouch,
        syncTouchLerp: CONFIG.syncTouchLerp,
      });

      // ============================================================
      // GSAP SCROLLTRIGGER INTEGRATION
      // Critical: This syncs Lenis with ScrollTrigger
      // ============================================================
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        lenis.on('scroll', ScrollTrigger.update);

        gsapTickerCb = (time) => {
          if (lenis) lenis.raf(time * 1000);
        };
        gsap.ticker.add(gsapTickerCb);

        gsap.ticker.lagSmoothing(0);
      } else {
        // Fallback: Use native RAF if GSAP not available
        function raf(time) {
          lenis.raf(time);
          rafId = requestAnimationFrame(raf);
        }
        rafId = requestAnimationFrame(raf);
      }

      // ============================================================
      // ANCHOR LINK HANDLING
      // Smooth scroll to anchor targets
      // ============================================================
      setupAnchorLinks();

      // ============================================================
      // DYNAMIC CONTENT HANDLING
      // ============================================================
      setupResizeObserver();

      // ============================================================
      // SCROLL LOCK METHODS
      // For modals, drawers, etc.
      // ============================================================
      setupScrollLock();

      // ============================================================
      // MOBILE OPTIMIZATIONS
      // ============================================================
      setupMobileOptimizations();

      // Add lenis class to html
      document.documentElement.classList.add('lenis', 'lenis-smooth');
      
      isInitialized = true;
      
      console.log('[Smooth Scroll] Initialized successfully');
      
      // Dispatch custom event for other scripts
      window.dispatchEvent(new CustomEvent('lenisReady', { detail: { lenis } }));

    } catch (error) {
      console.error('[Smooth Scroll] Initialization failed:', error);
    }
  }

  // ============================================================
  // ANCHOR LINK HANDLING
  // ============================================================
  
  function setupAnchorLinks() {
    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      
      if (!anchor) return;
      
      const href = anchor.getAttribute('href');
      
      // Skip if just "#" or empty
      if (!href || href === '#') return;
      
      const target = document.querySelector(href);
      
      if (!target) return;
      
      e.preventDefault();
      
      // Get offset for fixed header
      const headerHeight = getHeaderHeight();
      
      // Scroll to target with offset
      lenis.scrollTo(target, {
        offset: -headerHeight,
        duration: CONFIG.duration,
        easing: CONFIG.easing,
        onComplete: () => {
          // Update URL hash without jumping
          history.pushState(null, '', href);
        }
      });
    });

    // Handle hash on page load
    if (window.location.hash) {
      const target = document.querySelector(window.location.hash);
      if (target) {
        // Wait for page to be ready
        setTimeout(() => {
          const headerHeight = getHeaderHeight();
          lenis.scrollTo(target, {
            offset: -headerHeight,
            duration: 0, // Instant on page load
          });
        }, 100);
      }
    }
  }

  /**
   * Get the height of the fixed header
   */
  function getHeaderHeight() {
    const header = document.querySelector('.header, header, [data-header]');
    if (!header) return 80; // Default fallback
    
    const styles = getComputedStyle(header);
    if (styles.position === 'fixed' || styles.position === 'sticky') {
      return header.offsetHeight;
    }
    
    return 0;
  }

  // ============================================================
  // DYNAMIC CONTENT / RESIZE HANDLING
  // ============================================================
  
  function setupResizeObserver() {
    // Debounced resize handler
    const handleResize = debounce(() => {
      if (lenis) {
        lenis.resize();
        
        // Also refresh ScrollTrigger
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      }
    }, 250);

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    // ResizeObserver for dynamic content
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(
        debounce((entries) => {
          if (lenis) {
            lenis.resize();
          }
        }, 100)
      );

      // Observe main content area
      const mainContent = document.querySelector('#MainContent, main, .content-for-layout');
      if (mainContent) {
        resizeObserver.observe(mainContent);
      }
    }

    // MutationObserver for DOM changes (Shopify AJAX content)
    const mutationObserver = new MutationObserver(
      debounce((mutations) => {
        // Check if any mutations affected layout
        const hasLayoutChange = mutations.some(mutation => 
          mutation.addedNodes.length > 0 || 
          mutation.removedNodes.length > 0
        );
        
        if (hasLayoutChange && lenis) {
          lenis.resize();
          
          if (typeof ScrollTrigger !== 'undefined') {
            ScrollTrigger.refresh();
          }
        }
      }, 200)
    );

    // Observe the main content for changes
    const mainContent = document.querySelector('#MainContent, main');
    if (mainContent) {
      mutationObserver.observe(mainContent, {
        childList: true,
        subtree: true
      });
    }
  }

  // ============================================================
  // SCROLL LOCK (for modals, drawers)
  // ============================================================
  
  function setupScrollLock() {
    // Expose global methods for scroll locking
    window.SiteSmoothScroll = window.SiteSmoothScroll || {};
    
    window.SiteSmoothScroll.stop = () => {
      if (lenis) {
        lenis.stop();
        document.body.classList.add('scroll-locked');
      }
    };
    
    window.SiteSmoothScroll.start = () => {
      if (lenis) {
        lenis.start();
        document.body.classList.remove('scroll-locked');
      }
    };
    
    window.SiteSmoothScroll.scrollTo = (target, options = {}) => {
      if (lenis) {
        const headerHeight = getHeaderHeight();
        lenis.scrollTo(target, {
          offset: options.offset || -headerHeight,
          duration: options.duration || CONFIG.duration,
          easing: options.easing || CONFIG.easing,
          ...options
        });
      }
    };
    
    window.SiteSmoothScroll.resize = () => {
      if (lenis) {
        lenis.resize();
      }
    };
    
    window.SiteSmoothScroll.destroy = destroyLenis;
    
    window.SiteSmoothScroll.getInstance = () => lenis;

    // Listen for cart drawer events (Shopify)
    document.addEventListener('cart:open', () => {
      window.SiteSmoothScroll.stop();
    });
    
    document.addEventListener('cart:close', () => {
      window.SiteSmoothScroll.start();
    });

    // Listen for modal events
    document.addEventListener('modal:open', () => {
      window.SiteSmoothScroll.stop();
    });
    
    document.addEventListener('modal:close', () => {
      window.SiteSmoothScroll.start();
    });
  }

  // ============================================================
  // MOBILE OPTIMIZATIONS
  // ============================================================
  
  function setupMobileOptimizations() {
    // Detect mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      document.documentElement.classList.add('is-mobile');
      
      // Slightly increase lerp on mobile for more responsive feel
      if (lenis) {
        // Mobile users expect more direct control
        // No config change needed if syncTouch is enabled
      }
    }

    // Handle iOS Safari viewport height issues
    function setVH() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
    
    setVH();
    window.addEventListener('resize', debounce(setVH, 100));

    // Prevent overscroll on iOS
    document.body.addEventListener('touchmove', (e) => {
      if (document.body.classList.contains('scroll-locked')) {
        if (!e.target.closest('[data-lenis-prevent]')) {
          e.preventDefault();
        }
      }
    }, { passive: false });
  }

  // ============================================================
  // CLEANUP / DESTROY
  // ============================================================
  
  function destroyLenis() {
    if (gsapTickerCb && typeof gsap !== 'undefined') {
      gsap.ticker.remove(gsapTickerCb);
      gsapTickerCb = null;
    }

    if (lenis) {
      lenis.destroy();
      lenis = null;
    }
    
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    
    document.documentElement.classList.remove('lenis', 'lenis-smooth');
    isInitialized = false;
    
    console.log('[Smooth Scroll] Destroyed');
  }

  // ============================================================
  // REDUCED MOTION LISTENER
  // ============================================================
  
  function setupReducedMotionListener() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    mediaQuery.addEventListener('change', (e) => {
      if (e.matches) {
        destroyLenis();
        document.documentElement.classList.add('reduced-motion');
      } else {
        document.documentElement.classList.remove('reduced-motion');
        initLenis();
      }
    });
  }

  // ============================================================
  // VIEWPORT CHANGE LISTENER
  // Destroy Lenis when entering mobile, re-init when returning to desktop
  // ============================================================

  function setupViewportListener() {
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    mobileQuery.addEventListener('change', (e) => {
      if (e.matches) {
        destroyLenis();
      } else if (!isInitialized && !isReducedMotion) {
        initLenis();
      }
    });
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================
  
  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initLenis();
      setupReducedMotionListener();
      setupViewportListener();
    });
  } else {
    initLenis();
    setupReducedMotionListener();
    setupViewportListener();
  }

  // Re-init on Shopify section refresh (Theme Editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      if (lenis) {
        setTimeout(() => {
          lenis.resize();
          if (typeof ScrollTrigger !== 'undefined') {
            ScrollTrigger.refresh();
          }
        }, 100);
      }
    });
  }

})();

